import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Check, TriangleAlert } from 'lucide-react';
import Preloader from '@/components/Preloader';
import PassosDaFolha from './PassosDaFolha';
import { useConferencia, useFolha } from './useFolha';

const TOM = {
  ok: { classe: 'border-success/40 bg-success/5', icone: Check, cor: 'text-success' },
  atencao: { classe: 'border-warning/50 bg-warning/5', icone: TriangleAlert, cor: 'text-warning' },
  erro: { classe: 'border-danger/50 bg-danger/5', icone: AlertTriangle, cor: 'text-danger' },
};

/**
 * As sete conferências que hoje ele faz de olho, no fim de um dia de
 * digitação: cada setor contra o total impresso, a soma contra o resumo geral
 * e contra o totalizador, e a prova da conta de salários a pagar.
 */
export default function ConferenciaPage() {
  const { folhaId, data: folha, isLoading } = useFolha();
  const { data: conferencia, isLoading: conferindo } = useConferencia(folhaId, Boolean(folha));

  if (isLoading) {
    return <Preloader telaCheia />;
  }

  if (!folha) {
    return <p className="px-lg py-lg text-[14px] text-danger">Não foi possível abrir esta competência. Ela pode ter sido excluída.</p>;
  }

  return (
    <div className="px-lg py-lg">
      <header className="mb-lg">
        <h1 className="text-[22px] font-bold text-ink">Conferência</h1>
        <p className="mt-xs text-[13px] text-muted">
          Sete checagens automáticas antes de a planilha ir para a contabilidade. Os lançamentos se recalculam sozinhos a
          cada arquivo recebido e a cada mudança nos cadastros.
        </p>
      </header>

      <PassosDaFolha folha={folha} conferencia={conferencia} atual="conferencia" />

      {conferindo && (
        <div className="flex flex-col items-center gap-sm py-2xl text-[13px] text-muted">
          <Preloader />
          Conferindo a folha…
        </div>
      )}

      <ul className="grid gap-md lg:grid-cols-2">
        {(conferencia?.checagens ?? []).map((checagem) => {
          const tom = TOM[checagem.situacao];
          const Icone = tom.icone;

          return (
            <li key={checagem.chave} className={`rounded-lg border p-md ${tom.classe}`}>
              <p className="flex items-start gap-sm text-[14px] font-bold text-ink">
                <Icone size={17} className={`mt-[1px] shrink-0 ${tom.cor}`} />
                {checagem.titulo}
              </p>
              <p className="mt-xs pl-[25px] text-[13px] text-muted">{checagem.detalhe}</p>

              {checagem.problemas.length > 6 && (
                <p className="mt-xs pl-[25px] text-[12px] text-muted">e mais {checagem.problemas.length - 6} itens</p>
              )}

              {checagem.chave === 'cobertura' && checagem.situacao === 'erro' && (
                <p className="mt-sm flex flex-wrap gap-md pl-[25px] text-[13px] font-bold">
                  <Link to="/centros-de-custo" className="text-accent hover:underline">
                    Centros de custo
                  </Link>
                  <Link to="/rubricas-e-verbas" className="text-accent hover:underline">
                    Rubricas e verbas
                  </Link>
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {conferencia?.aprovada && (
        <Link
          to={`/folha/${folha.id}/planilha`}
          className="mt-lg flex h-11 w-full items-center justify-center gap-sm rounded-lg bg-accent text-[15px] font-bold text-white hover:bg-accent/90 sm:w-auto sm:px-lg"
        >
          Ver os lançamentos e baixar a planilha
          <ArrowRight size={16} />
        </Link>
      )}
    </div>
  );
}
